import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import type { FormikProps } from 'formik';
import {
    Checkbox,
    Grid,
    Box,
    FormControlLabel,
    Stack,
    FormHelperText,
    TextField,
    Autocomplete,
    IconButton,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';

// Icons
import SaveIcon from '@mui/icons-material/Save';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import RestoreIcon from '@mui/icons-material/Restore';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// Components
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { ContentCard, CardType, CardTab, HeaderTabs } from 'src/components/organisms/ContentCard';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import TooltipField from 'src/components/atoms/TooltipField';
import AiButton from 'src/components/molecules/ai/AiButton';

// Hooks & Utils
import useSubmit from 'src/hooks/forms/useSubmit';
import useRedirectOnCreate from 'src/hooks/forms/useRedirectOnCreate';
import { buildSupplierRequest } from 'src/utils/submit/submitRequests';
import FormHelper from 'src/utils/formHelper';
import { useSupplierOptions } from 'src/hooks/forms/useOptions';
import { useSupplierForm } from 'src/hooks/forms/useForms';
import AddressStreetAutocompleteField from 'src/components/molecules/formik/AddressStreetAutocompleteField';
import { FormInfoButton } from 'src/components/atoms/buttons/FormInfoButton';
import { useVideoDialog, VideoButton, VideoDialog } from 'src/components/molecules/video-dialog/VideoDialog';
import { COMPANY_NAME } from 'src/utils/enviromentValues';

const requiredAddressFields = [
    'Address_Country',
    'Address_Street',
    'Address_City',
    'Address_Postcode',
];

const requiredSupplierFields = [
    'Name',
    'CurrencyId',
    ...requiredAddressFields,
];

const schema = Yup.object().shape({
    Name: Yup.string()
        .required('Enter the supplier name used on orders and documents.'),
    CurrencyId: Yup.string()
        .required('Select the default currency you use with this supplier.'),
    StandardLeadTime: Yup.number()
        .nullable()
        .typeError('Enter lead time in days or leave empty.'),
    Address_Country: Yup.string()
        .required('Select the country for this supplier address.'),
    Address_Street: Yup.string()
        .required('Enter the street address for this supplier.'),
    Address_City: Yup.string()
        .required('Enter the city for this supplier address.'),
    Address_Postcode: Yup.string()
        .required('Enter the postcode for this supplier address.'),
});

const defaultInitialValues: any = {
    Name: "",
    ShortName: "",
    CorporateRegistrationNumber: "",
    Description: "",
    CurrencyId: "",
    OurCustomerNumber: "",
    StandardLeadTime: "",
    DropShipEnabled: false,
    Disabled: false,

    Address_Salutation: "",
    Address_Title: "",
    Address_FirstName: "",
    Address_LastName: "",
    Email: "",
    Mobile: "",

    Address_Company: "",
    Address_Country: "",
    Address_Street: "",
    Address_StreetAdditionalInformation: "",
    Address_City: "",
    Address_State: "",
    Address_Postcode: "",
    Address_Department: "",
    Address_EmailAddress: "",
    Address_PhoneNumber: "",
    Address_AdditionalInformation: "",

    ShippingMethodId: "",
    PaymentMethodId: "",
    ShippingTermId: "",
    PaymentTermId: "",
    SendTypeId: 1,
    SendDestination: "",
    ShippingTermsDestination: "",

    VendorProductName: "",
    Product: "",
    isEditPage: "",
};

function EditSupplierAiButtons() {
    const [editId, PageName, , ,] = FormHelper.getEditPageConfig("Supplier", buildSupplierRequest, buildSupplierRequest);
    const initialValues = useSupplierForm(editId, defaultInitialValues);
    const [Currencies, ShippingMethods, ShippingMethodsDropShip, ShippingTerms, PaymentMethods, PaymentTerms, OrderSendTypes, countriesOptions] = useSupplierOptions();

    const [result, submit] = useSubmit(buildSupplierRequest);
    useRedirectOnCreate(result, editId, "Id", "/products/suppliers/edit-test?id=");

    const [currentTab, setCurrentTab] = useState<string>('Defaults');
    const [addressTabHasSubmitErrors, setAddressTabHasSubmitErrors] = useState<boolean>(false);
    const videoRef = useVideoDialog("https://www.youtube.com/embed/-CYYG3aqSh4?si=bJj5WWaIlsn8Rief");

    return (
        <>
            <Formik
                initialValues={initialValues}
                validateOnBlur={true}
                validateOnChange={false}
                validationSchema={schema}
                onSubmit={submit}
                enableReinitialize
            >
                {(props): JSX.Element => {
                    const addressTabHasErrors = addressTabHasSubmitErrors || requiredAddressFields.some((fieldName) => Boolean((props.errors as any)?.[fieldName]));
                    const addressTabLabel = addressTabHasErrors ? '⚠ Address & Location' : 'Address & Location';

                    const handleSave = async () => {
                        const errors = await props.validateForm();

                        const touchedFields = requiredSupplierFields.reduce((acc: any, fieldName) => {
                            acc[fieldName] = true;
                            return acc;
                        }, {});

                        props.setTouched({ ...props.touched, ...touchedFields }, true);

                        const hasAddressErrors = requiredAddressFields.some((fieldName) => Boolean((errors as any)?.[fieldName]));
                        setAddressTabHasSubmitErrors(hasAddressErrors);

                        props.handleSubmit();
                    };

                    // Tab Configuration
                    const tabs: CardTab[] = [
                        { value: 'Defaults', label: 'Order Defaults' },
                        { value: 'Address', label: addressTabLabel },
                        { value: 'System', label: 'System Info' }
                    ];

                    return (
                        <SupplierLlmClipboardProvider
                            formikProps={props}
                            pageContext={{
                                entityName: "Supplier",
                                pageName: getPageNameText(PageName),
                                editId,
                                currentTab,
                                initialValues,
                            }}
                            supplierOptionsContext={{
                                Currencies,
                                ShippingMethods,
                                ShippingMethodsDropShip,
                                ShippingTerms,
                                PaymentMethods,
                                PaymentTerms,
                                OrderSendTypes,
                                countriesOptions,
                            }}
                        >
                            <div>
                                <PageHeader {...{
                                    title: PageName,
                                    initialValues: initialValues,
                                    subTitle: "Create and edit Supplier",
                                    components: [
                                        { component: <VideoButton videoRef={videoRef} /> },
                                        { component: (<FormInfoButton formikProps={props} />), },
                                        { component: <SupplierLlmCopyButton /> },
                                        { component: <SupplierLlmPasteButton /> },
                                        { component: <SupplierLlmAiConfigButton /> },
                                        { component: <SupplierLlmAiRunButton /> },
                                        { component: <CustomButton isSubmitting={props.isSubmitting} onClick={handleSave} title="Save" icon={<SaveIcon />} /> },
                                        { isInGroupedMenu: true, component: <CustomButton variant="text" title="New Supplier" icon={<AddTwoToneIcon />} actionUrl="/products/suppliers/create" /> },
                                    ],
                                    alertData: { dataArray: (result ?? false) ? [result] : null }
                                }} />
                                <VideoDialog videoRef={videoRef} />

                                <ContentCardsContainer>

                                    {/* --- ROW 1: Three Tall, Narrow Cards (md=4 each) --- */}

                                    {/* Card 1: Identity */}
                                    <ContentCard cardId="Supplier Identity" md={4}>
                                        <SupplierLlmFormContainer formikProps={props}>
                                            <SupplierLlmFormField fieldProps={{ name: "Name", className: "flash-highlight", label: "Supplier Name", md: 12 }} formikProps={props} />
                                            <SupplierLlmFormField
                                                fieldProps={{
                                                    name: "ShortName",
                                                    label: "Short Name",
                                                    md: 12,
                                                    tooltipText: "Used internally to quickly find and work with this supplier.",
                                                }}
                                                formikProps={props}
                                            />
                                            <SupplierLlmFormField fieldProps={{ name: "CorporateRegistrationNumber", label: "Corporate Reg. No.", md: 12 }} formikProps={props} />
                                            <SupplierLlmFormField fieldProps={{ name: "Description", label: "Description", md: 12, multiline: true, rows: 4 }} formikProps={props} />
                                        </SupplierLlmFormContainer>
                                    </ContentCard>

                                    {/* Card 2: Configuration/Settings */}
                                    <ContentCard cardId="Settings" md={4}>
                                        <SupplierLlmFormContainer formikProps={props}>
                                            <SupplierLlmFormField fieldProps={{ name: "CurrencyId", className: "flash-highlight", select: true, label: "Currency", md: 12 }} formikProps={props}>
                                                {Currencies?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                            </SupplierLlmFormField>
                                            <SupplierLlmFormField fieldProps={{ name: "OurCustomerNumber", label: "Our Customer Number", md: 12 }} formikProps={props} />
                                            <SupplierLlmFormField fieldProps={{ name: "StandardLeadTime", label: "Lead Time (Days)", md: 12 }} formikProps={props} />

                                            <Grid item md={12} pt={2}>
                                                <SupplierLlmFormField fieldProps={{ name: "DropShipEnabled", checkbox: true, label: "DropShip Enabled", md: 12 }} formikProps={props} />
                                                <SupplierLlmFormField fieldProps={{ name: "Disabled", checkbox: true, label: "Supplier Disabled", md: 12 }} formikProps={props} />
                                            </Grid>
                                        </SupplierLlmFormContainer>
                                    </ContentCard>

                                    {/* Card 3: Contact Person */}
                                    <ContentCard cardId="Main Contact" md={4}>
                                        <SupplierLlmFormContainer formikProps={props}>
                                            <SupplierLlmFormField fieldProps={{ name: "Address_Salutation", label: "Salutation", md: 5.8 }} formikProps={props} />
                                            <SupplierLlmFormField fieldProps={{ name: "Address_Title", label: "Job Title", md: 5.8 }} formikProps={props} />

                                            <SupplierLlmFormField fieldProps={{ name: "Address_FirstName", label: "First Name", md: 5.8 }} formikProps={props} />
                                            <SupplierLlmFormField fieldProps={{ name: "Address_LastName", label: "Last Name", md: 5.8 }} formikProps={props} />

                                            <SupplierLlmFormField fieldProps={{ name: "Email", label: "Contact Email", md: 12 }} formikProps={props} />
                                            <SupplierLlmFormField fieldProps={{ name: "Mobile", label: "Mobile", md: 12 }} formikProps={props} />
                                        </SupplierLlmFormContainer>
                                    </ContentCard>

                                    {/* --- ROW 2: Tab Container for Heavy Details --- */}
                                    <ContentCard {...{
                                        cardId: "TabContainer",
                                        contentPadding: 0,
                                        md: 12,
                                        cardTypes: [CardType.tabContainer],
                                        cardHeader: <HeaderTabs {...{ tabs, currentTab, setCurrentTab }} />
                                    }}>

                                        {/* TAB 1: ADDRESS */}
                                        {currentTab === "Address" && (
                                            <ContentCard cardId="Address" cardTypes={[CardType.tab]} contentPadding={2}>
                                                <SupplierLlmFormContainer formikProps={props}>
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_Company", className: "flash-highlight", label: "Company / Location Name", md: 12 }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_Country", className: "flash-highlight", select: true, label: "Country", md: 5.8 }} formikProps={props}>
                                                        {countriesOptions?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                    </SupplierLlmFormField>
                                                    {/* Restored 5.8 / 3.8 Spacing */}
                                                    <AddressStreetAutocompleteField
                                                        formikProps={props}
                                                        config={{
                                                            streetField: 'Address_Street',
                                                            countryField: 'Address_Country',
                                                            cityField: 'Address_City',
                                                            stateField: 'Address_State',
                                                            postcodeField: 'Address_Postcode',
                                                        }}
                                                        countryOptions={countriesOptions}
                                                        md={7.8}
                                                        xs={5.8}
                                                        label="Street Address"
                                                    />
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_StreetAdditionalInformation", label: "Unit / Suite", md: 3.8 }} formikProps={props} />

                                                    <SupplierLlmFormField fieldProps={{ name: "Address_City", className: "flash-highlight", label: "City", md: 3.8 }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_State", label: "State", md: 3.8 }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_Postcode", className: "flash-highlight", label: "Postcode", md: 3.8 }} formikProps={props} />

                                                    <SupplierLlmFormField fieldProps={{ name: "Address_Department", label: "Department", md: 5.8 }} formikProps={props} />

                                                    <SupplierLlmFormField fieldProps={{ name: "Address_EmailAddress", label: "General Email", md: 5.8 }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Address_PhoneNumber", label: "General Phone", md: 5.8 }} formikProps={props} />

                                                    <SupplierLlmFormField fieldProps={{ name: "Address_AdditionalInformation", label: "Notes / Door Code", md: 12 }} formikProps={props} />
                                                </SupplierLlmFormContainer>
                                            </ContentCard>
                                        )}

                                        {/* TAB 2: ORDER DEFAULTS */}
                                        {currentTab === "Defaults" && (
                                            <ContentCard cardId="Defaults" cardTypes={[CardType.tab]} contentPadding={2}>
                                                <SupplierLlmFormContainer formikProps={props}>
                                                    <SupplierLlmFormField
                                                        fieldProps={{
                                                            name: "ShippingMethodId",
                                                            select: true,
                                                            label: "Shipping Method",
                                                            md: 5.8,
                                                            warning: "Set a default for normal orders. Empty keeps this supplier as a draft.",
                                                        }}
                                                        formikProps={props}
                                                    >
                                                        {(props.values.DropShipEnabled ? ShippingMethodsDropShip : ShippingMethods)?.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                        ))}
                                                    </SupplierLlmFormField>

                                                    <SupplierLlmFormField
                                                        fieldProps={{
                                                            name: "PaymentMethodId",
                                                            select: true,
                                                            label: "Payment Method",
                                                            md: 5.8,
                                                            warning: "Recommended for live use. Empty means this supplier is still a draft.",
                                                        }}
                                                        formikProps={props}
                                                    >
                                                        {PaymentMethods?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                    </SupplierLlmFormField>

                                                    <SupplierLlmFormField
                                                        fieldProps={{
                                                            name: "ShippingTermId",
                                                            select: true,
                                                            label: "Shipping Term",
                                                            md: 5.8,
                                                            warning: "Usually set on real suppliers. Empty is treated as draft.",
                                                        }}
                                                        formikProps={props}
                                                    >
                                                        {ShippingTerms?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                    </SupplierLlmFormField>

                                                    <SupplierLlmFormField
                                                        fieldProps={{
                                                            name: "PaymentTermId",
                                                            select: true,
                                                            label: "Payment Term",
                                                            md: 5.8,
                                                            warning: "Set before using this supplier in production. Empty keeps it as draft.",
                                                        }}
                                                        formikProps={props}
                                                    >
                                                        {PaymentTerms?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                    </SupplierLlmFormField>

                                                    <SupplierLlmFormField fieldProps={{ name: "SendTypeId", select: true, label: "Send Type", md: 3.8 }} formikProps={props}>
                                                        {OrderSendTypes?.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                                                    </SupplierLlmFormField>
                                                    <SupplierLlmFormField fieldProps={{ name: "SendDestination", label: "Send Destination", md: 3.8 }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "ShippingTermsDestination", label: "Ship Terms Dest.", md: 3.8 }} formikProps={props} />
                                                </SupplierLlmFormContainer>
                                            </ContentCard>
                                        )}

                                        {/* TAB 3: SYSTEM INFO (Metadata) */}
                                        {currentTab === "System" && (
                                            <ContentCard cardId="System" cardTypes={[CardType.tab]} contentPadding={2}>
                                                <SupplierLlmFormContainer formikProps={props}>
                                                    <SupplierLlmFormField fieldProps={{ name: "Created", date: true, label: "Created Date", md: 3.8, disabled: true }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Modified", date: true, label: "Last Modified", md: 3.8, disabled: true }} formikProps={props} />
                                                    <SupplierLlmFormField fieldProps={{ name: "Id", label: "System ID", md: 3.8, disabled: true }} formikProps={props} />
                                                </SupplierLlmFormContainer>
                                            </ContentCard>
                                        )}

                                    </ContentCard>

                                </ContentCardsContainer>
                            </div>
                        </SupplierLlmClipboardProvider>
                    );
                }}
            </Formik>
            <Footer />
        </>
    );
}

type SupplierLlmOptionsContext = {
    Currencies: any;
    ShippingMethods: any;
    ShippingMethodsDropShip: any;
    ShippingTerms: any;
    PaymentMethods: any;
    PaymentTerms: any;
    OrderSendTypes: any;
    countriesOptions: any;
};

type SupplierLlmPageContext = {
    entityName: string;
    pageName: string;
    editId: any;
    currentTab: string;
    initialValues: any;
};

type SupplierLlmChangedField = {
    fieldName: string;
    originalValue: any;
    pastedValue: any;
};

type SupplierLlmChangedFields = Record<string, SupplierLlmChangedField>;

type SupplierLlmClipboardController = {
    copyPromptToClipboard: (event?: React.MouseEvent) => Promise<void>;
    pasteValuesFromClipboard: (event?: React.MouseEvent) => Promise<void>;
    applyLlmResponseText: (responseText: string) => Promise<SupplierLlmApplyResult>;
    buildPrompt: () => string;
    revertField: (fieldName: string) => Promise<void>;
    copyButtonTitle: string;
    pasteButtonTitle: string;
    changedFields: SupplierLlmChangedFields;
};

type SupplierLlmApplyResult = 'applied' | 'no-fields' | 'no-changes';

type UseSupplierLlmClipboardArgs = {
    formikProps: FormikProps<any>;
    pageContext: SupplierLlmPageContext;
    supplierOptionsContext: SupplierLlmOptionsContext;
    additionalContext?: Record<string, unknown>;
};

type SupplierLlmClipboardProviderProps = UseSupplierLlmClipboardArgs & {
    children: React.ReactNode;
};

const SupplierLlmClipboardContext = React.createContext<SupplierLlmClipboardController | null>(null);

const supplierLlmReadOnlyFields = [
    "Id",
    "Created",
    "Modified",
    "isEditPage",
];

const supplierLlmEditableFields = [
    "Name",
    "ShortName",
    "CorporateRegistrationNumber",
    "Description",
    "CurrencyId",
    "OurCustomerNumber",
    "StandardLeadTime",
    "DropShipEnabled",
    "Disabled",
    "Address_Salutation",
    "Address_Title",
    "Address_FirstName",
    "Address_LastName",
    "Email",
    "Mobile",
    "Address_Company",
    "Address_Country",
    "Address_Street",
    "Address_StreetAdditionalInformation",
    "Address_City",
    "Address_State",
    "Address_Postcode",
    "Address_Department",
    "Address_EmailAddress",
    "Address_PhoneNumber",
    "Address_AdditionalInformation",
    "ShippingMethodId",
    "PaymentMethodId",
    "ShippingTermId",
    "PaymentTermId",
    "SendTypeId",
    "SendDestination",
    "ShippingTermsDestination",
    "VendorProductName",
    "Product",
];

const commonLlmJsonReturnPrompt = `
Return only valid JSON. Do not use markdown, codeblocks, explanations, or links.

Expected shape:
{
  "values": {
    "Name": "Updated supplier name",
    "CurrencyId": "value-from-available-options"
  }
}

Rules:
- Only include fields that should be changed.
- When creating a new supplier, include all required fields listed in requiredFields when you have enough data.
- The keys inside "values" must match the Formik field names exactly.
- Keep booleans as booleans, numbers as numbers, and strings as strings.
- Do not include read-only/system fields unless explicitly asked.
`.trim();

const commonLlmOptionsPrompt = `
When a field has available options, use the option value in the returned JSON, not the display label.
Things like CurrencyId, ShippingMethodId, ShippingTermId, PaymentMethodId, PaymentTermId, SendTypeId, and Address_Country require IDs/option values, not labels; find those IDs in fieldOptions in the attached context data.
Compact option objects use this shape: { "Display label": value }.
ShippingMethodId options depend on DropShipEnabled. Use fieldOptions.ShippingMethodId_DropShip when DropShipEnabled is true, otherwise use fieldOptions.ShippingMethodId_Normal.
If the correct option is unclear, keep the existing value instead of guessing. 
`.trim();

const supplierLlmPrompt = `
You are helping update a Supplier form. The company ${COMPANY_NAME} IS NOT the supplier, but the user is editing a supplier for ${COMPANY_NAME}.
Use the stable supplier form rules, available select options, current form values, original initial values, required fields, and page context below.
Return a JSON object that can be pasted back into the form to update the supplier.
`.trim();

function SupplierLlmClipboardProvider({ children, ...hookArgs }: SupplierLlmClipboardProviderProps) {
    const supplierLlmClipboard = useSupplierLlmClipboard(hookArgs);

    return (
        <SupplierLlmClipboardContext.Provider value={supplierLlmClipboard}>
            {children}
        </SupplierLlmClipboardContext.Provider>
    );
}

function useSupplierLlmClipboardContext() {
    const supplierLlmClipboard = useContext(SupplierLlmClipboardContext);

    if (!supplierLlmClipboard) {
        throw new Error("SupplierLlmClipboardContext is missing.");
    }

    return supplierLlmClipboard;
}

function SupplierLlmCopyButton() {
    const supplierLlmClipboard = useSupplierLlmClipboardContext();

    return <CustomButton variant="text" title={supplierLlmClipboard.copyButtonTitle} icon={<ContentCopyIcon />} onClick={supplierLlmClipboard.copyPromptToClipboard} />;
}

function SupplierLlmPasteButton() {
    const supplierLlmClipboard = useSupplierLlmClipboardContext();

    return <CustomButton variant="text" title={supplierLlmClipboard.pasteButtonTitle} icon={<ContentPasteIcon />} onClick={supplierLlmClipboard.pasteValuesFromClipboard} />;
}

function SupplierLlmAiConfigButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Config" icon={<SettingsIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <SupplierLlmAiConfigDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}

function SupplierLlmAiRunButton() {
    const supplierLlmClipboard = useSupplierLlmClipboardContext();
    const [open, setOpen] = useState(false);

    return (
        <>
            <CustomButton variant="text" title="AI Run" icon={<AutoAwesomeIcon />} onClick={(event?: React.MouseEvent) => {
                event?.preventDefault();
                setOpen(true);
            }} />
            <SupplierLlmAiRunDialog
                open={open}
                onClose={() => setOpen(false)}
                buildPrompt={supplierLlmClipboard.buildPrompt}
                applyLlmResponseText={supplierLlmClipboard.applyLlmResponseText}
            />
        </>
    );
}

function useSupplierLlmClipboard({ formikProps, pageContext, supplierOptionsContext, additionalContext = {} }: UseSupplierLlmClipboardArgs): SupplierLlmClipboardController {
    const [copyButtonTitle, setCopyButtonTitle] = useState<string>("Copy");
    const [pasteButtonTitle, setPasteButtonTitle] = useState<string>("Paste");
    const [changedFields, setChangedFields] = useState<SupplierLlmChangedFields>({});

    const setTemporaryButtonTitle = useCallback((setter: React.Dispatch<React.SetStateAction<string>>, temporaryTitle: string, defaultTitle: string) => {
        setter(temporaryTitle);
        window.setTimeout(() => setter(defaultTitle), 1800);
    }, []);

    const getPromptData = useCallback(() => {
        const fieldOptions = {
            CurrencyId: compactSupplierLabelValueOptions(supplierOptionsContext.Currencies),
            ShippingMethodId_Normal: compactSupplierLabelValueOptions(supplierOptionsContext.ShippingMethods),
            ShippingMethodId_DropShip: compactSupplierLabelValueOptions(supplierOptionsContext.ShippingMethodsDropShip),
            ShippingTermId: compactSupplierLabelValueOptions(supplierOptionsContext.ShippingTerms),
            PaymentMethodId: compactSupplierLabelValueOptions(supplierOptionsContext.PaymentMethods),
            PaymentTermId: compactSupplierLabelValueOptions(supplierOptionsContext.PaymentTerms),
            SendTypeId: compactSupplierLabelValueOptions(supplierOptionsContext.OrderSendTypes),
            Address_Country: compactSupplierLabelValueOptions(supplierOptionsContext.countriesOptions),
        };

        return {
            pageContext: {
                entityName: pageContext.entityName,
                pageName: pageContext.pageName,
            },
            requiredFields: {
                supplier: requiredSupplierFields,
                address: requiredAddressFields,
            },
            fieldRequirements: {
                Name: 'Required. Supplier name used on orders and documents.',
                CurrencyId: 'Required. Use the value from fieldOptions.CurrencyId.',
                Address_Country: 'Required. Use the value from fieldOptions.Address_Country.',
                Address_Street: 'Required. Street address for this supplier.',
                Address_City: 'Required. City for this supplier address.',
                Address_Postcode: 'Required. Postcode for this supplier address.',
                StandardLeadTime: 'Optional. Lead time in days. Do not invent if unknown.',
                ShippingMethodId: 'Optional. Use ShippingMethodId_DropShip when DropShipEnabled is true, otherwise ShippingMethodId_Normal.',
                PaymentMethodId: 'Optional. Use the value from fieldOptions.PaymentMethodId.',
                ShippingTermId: 'Optional. Use the value from fieldOptions.ShippingTermId.',
                PaymentTermId: 'Optional. Use the value from fieldOptions.PaymentTermId.',
                SendTypeId: 'Optional. Use the value from fieldOptions.SendTypeId.',
            },
            editableFields: supplierLlmEditableFields,
            readOnlyFields: supplierLlmReadOnlyFields,
            fieldOptions,
            compactDataShapes: {
                fieldOptions: '{ "Display label": value }',
                requiredFields: '{ "supplier": string[], "address": string[] }',
            },
            currentValues: compactSupplierValues(formikProps.values),
            initialValues: compactSupplierValues(pageContext.initialValues),
            changedFields,
            pageState: {
                editId: pageContext.editId,
                currentTab: pageContext.currentTab,
            },
            additionalContext,
        };
    }, [additionalContext, changedFields, formikProps.values, pageContext, supplierOptionsContext]);

    const buildPrompt = useCallback(() => {
        return [
            supplierLlmPrompt,
            commonLlmJsonReturnPrompt,
            commonLlmOptionsPrompt,
            "Context data:",
            JSON.stringify(getPromptData(), null, 2),
        ].join("\n\n");
    }, [getPromptData]);

    const copyPromptToClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            await writeTextToClipboard(buildPrompt());
            setTemporaryButtonTitle(setCopyButtonTitle, "Copied", "Copy");
        } catch (error) {
            console.error("Failed to copy supplier LLM prompt.", error);
            setTemporaryButtonTitle(setCopyButtonTitle, "Copy failed", "Copy");
            window.alert("Could not copy the supplier prompt to the clipboard.");
        }
    }, [buildPrompt, setTemporaryButtonTitle]);

    const applyLlmResponseText = useCallback(async (responseText: string): Promise<SupplierLlmApplyResult> => {
        const parsedResponse = parseJsonFromClipboardText(responseText);
        const nextValues = getFormikValuesFromLlmResponse(parsedResponse);
        const allowedValues = getAllowedFormikValues(nextValues, formikProps.values);

        if (Object.keys(allowedValues).length === 0) {
            return 'no-fields';
        }

        const currentValues = formikProps.values;
        const changedAllowedValues = getChangedFormikValues(allowedValues, currentValues);

        if (Object.keys(changedAllowedValues).length === 0) {
            return 'no-changes';
        }

        const nextChangedFields = getNextSupplierLlmChangedFields(changedFields, changedAllowedValues, currentValues);

        await formikProps.setValues({ ...currentValues, ...changedAllowedValues }, true);
        setChangedFields(nextChangedFields);

        return 'applied';
    }, [changedFields, formikProps]);

    const pasteValuesFromClipboard = useCallback(async (event?: React.MouseEvent) => {
        event?.preventDefault();

        try {
            if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
                throw new Error("Clipboard read is not available in this browser.");
            }

            const clipboardText = await navigator.clipboard.readText();
            const applyResult = await applyLlmResponseText(clipboardText);

            if (applyResult === 'no-fields') {
                window.alert('No matching Formik fields were found in the pasted JSON. Expected either { "values": { ... } } or a JSON object with Formik field names.');
                return;
            }

            if (applyResult === 'no-changes') {
                setTemporaryButtonTitle(setPasteButtonTitle, "No changes", "Paste");
                return;
            }

            setTemporaryButtonTitle(setPasteButtonTitle, "Pasted", "Paste");
        } catch (error) {
            console.error("Failed to paste supplier LLM values.", error);
            setTemporaryButtonTitle(setPasteButtonTitle, "Paste failed", "Paste");
            window.alert("Could not paste supplier values. Make sure the clipboard contains valid JSON from the LLM.");
        }
    }, [applyLlmResponseText, setTemporaryButtonTitle]);

    const revertField = useCallback(async (fieldName: string) => {
        const change = changedFields[fieldName];

        if (!change) {
            return;
        }

        await formikProps.setFieldValue(fieldName, change.originalValue, true);

        setChangedFields((previousChangedFields) => {
            const nextChangedFields = { ...previousChangedFields };
            delete nextChangedFields[fieldName];
            return nextChangedFields;
        });
    }, [changedFields, formikProps]);

    return {
        copyPromptToClipboard,
        pasteValuesFromClipboard,
        applyLlmResponseText,
        buildPrompt,
        revertField,
        copyButtonTitle,
        pasteButtonTitle,
        changedFields,
    };
}

/**
 * Prototype local copy of the form container behavior.
 * Kept local so this feature can be tested without touching the shared FormContainerMolecule.
 */
function SupplierLlmFormContainer({ formikProps, children }: { formikProps: FormikProps<any>; children: React.ReactNode }) {
    return (
        <Grid container spacing={2}>
            {children}
        </Grid>
    );
}

/**
 * Prototype local copy of FormField.
 * Kept local so the diff UI can be tested without changing the shared FormField.
 */
type SupplierLlmResolvedType = 'date' | 'checkbox' | 'select' | 'multiline' | 'text';

interface SupplierLlmFormFieldProps {
    fieldProps: any;
    formikProps?: any;
    children?: React.ReactNode;
    sx?: SxProps<Theme>;
}

function SupplierLlmFormField({ fieldProps, formikProps, children, sx }: SupplierLlmFormFieldProps) {
    const label = fieldProps.label ?? (fieldProps.name?.charAt(0).toUpperCase() + fieldProps.name?.slice(1));
    const onChangeFunction = fieldProps?.handleChange ?? formikProps?.handleChange;
    const { t }: { t: any } = useTranslation();
    const supplierLlmClipboard = useSupplierLlmClipboardContext();

    const resolvedType: SupplierLlmResolvedType =
        fieldProps.type ??
        (fieldProps.date
            ? 'date'
            : fieldProps.checkbox
                ? 'checkbox'
                : fieldProps.select
                    ? 'select'
                    : fieldProps.multiLine || fieldProps.multiline
                        ? 'multiline'
                        : 'text');

    const name = fieldProps.name;
    const touched = formikProps?.touched?.[name];
    const error = formikProps?.errors?.[name];
    const hasSubmitted = (formikProps?.submitCount ?? 0) > 0;
    const showError = (touched || hasSubmitted) && error;

    // full status object
    const status = formikProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText: string | undefined = fieldProps.tooltipText;

    // Hints from formik.status.hints
    const hints = status?.hints ?? {};
    const hintForField = hints?.[name];
    const showHint = !showError && Boolean(hintForField);

    // Field meta (warning, etc) from schema
    const fieldMeta = status?.fieldMeta?.[name] ?? {};
    const metaWarning: string | undefined = fieldMeta.warning;

    const value = formikProps?.values?.[name];
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    const showWarning =
        !showError &&
        !showHint &&
        Boolean(metaWarning) &&
        (touched || hasSubmitted) &&
        isEmpty;

    const baseHelperText = showError
        ? error
        : showHint
            ? hintForField
            : showWarning
                ? metaWarning
                : '';

    // If info-button mode is on and there is tooltipText, use it as info helper text
    const useTooltipInfo = !baseHelperText && !showError && showAllTooltips && !!tooltipText;

    const helperText = useTooltipInfo ? tooltipText : baseHelperText;

    const isError = Boolean(showError);
    const isInfo = !isError && (showHint || useTooltipInfo);
    const isWarning = !isError && !isInfo && showWarning;

    const diffOptions = getSupplierLlmDiffOptions(fieldProps, formikProps, supplierLlmClipboard);

    return (
        <>
            <Grid
                item
                display="flex"
                md={fieldProps.md ?? 12}
                xs={fieldProps.xs ?? 5.8}
            >
                <Grid
                    item
                    md={12}
                    xs={12}
                    display="flex"
                    alignItems="center"
                    width="100%"
                >
                    {resolvedType === 'date' ? (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label={label}
                                ampm={false}
                                disabled={fieldProps.disabled ?? false}
                                value={
                                    formikProps?.values?.[fieldProps.name] &&
                                        formikProps?.values?.[fieldProps.name] !== ''
                                        ? parseISO(formikProps?.values?.[fieldProps.name])
                                        : null
                                }
                                onChange={(value) => {
                                    formikProps.setFieldValue(
                                        fieldProps.name,
                                        value ? new Date(value).toISOString() : null
                                    );
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        margin: 'normal',
                                        name: fieldProps.name,
                                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                            formikProps.setFieldTouched(fieldProps.name, true);
                                            formikProps.handleBlur(e);
                                        },
                                        error: Boolean(isError),
                                        helperText,
                                        sx,
                                        FormHelperTextProps: isInfo
                                            ? { sx: { color: 'info.main' } }
                                            : isWarning
                                                ? { sx: { color: 'warning.main' } }
                                                : undefined,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    ) : resolvedType === 'checkbox' ? (
                        <Box display="flex" alignItems="center">
                            <FormControlLabel
                                sx={sx}
                                control={
                                    <Checkbox
                                        name={fieldProps.name}
                                        checked={toBoolean(formikProps?.values?.[fieldProps.name]) ?? false}
                                        value={formikProps?.values?.[fieldProps.name] ?? false}
                                        onBlur={formikProps?.handleBlur}
                                        onChange={onChangeFunction}
                                    />
                                }
                                label={label}
                            />
                            <Stack display="flex" spacing={2}>
                                {Boolean(isError) && (
                                    <FormHelperText error>{helperText}</FormHelperText>
                                )}
                                {!isError && isInfo && (
                                    <FormHelperText sx={{ color: 'info.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                                {!isError && !isInfo && isWarning && (
                                    <FormHelperText sx={{ color: 'warning.main' }}>
                                        {helperText}
                                    </FormHelperText>
                                )}
                            </Stack>
                        </Box>
                    ) : (
                        <SupplierLlmCustomField
                            options={{
                                ...fieldProps,
                                ...diffOptions,
                                tooltipText: fieldProps.tooltipText,
                                select: fieldProps.select ?? (resolvedType === 'select'),
                                multiLine: fieldProps.multiLine ?? fieldProps.multiline ?? (resolvedType === 'multiline'),
                            }}
                            stateProps={formikProps}
                            sx={fieldProps.sx ?? sx}
                        >
                            {children}
                        </SupplierLlmCustomField>
                    )}

                    <TooltipField tooltipText={fieldProps.tooltipText} />
                    {fieldProps.aiField ? (
                        <AiButton
                            formikProps={formikProps}
                            fieldName={fieldProps.name}
                            fieldInstructions={fieldProps?.aiInstructions ?? null}
                        />
                    ) : null}
                </Grid>
            </Grid>
        </>
    );
}

/**
 * Prototype local copy of CustomField.
 * This is where the diff display is added directly to TextField.
 */
interface SupplierLlmCustomFieldProps {
    stateProps?: {
        values?: any;
        handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
        touched?: any;
        errors?: any;
        handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
        state?: any;
        setState?: React.Dispatch<React.SetStateAction<any>>;
        submitCount?: number;
        status?: any;
    };
    options: {
        label?: string;
        disabled?: boolean;
        multiLine?: boolean;
        multiline?: boolean;
        select?: boolean;
        isMulti?: boolean;
        type?: string;
        name: string;
        height?: string;
        topMargin?: string;
        selectOpen?: boolean;
        className?: string;
        setSelectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
        rows?: number;
        maxRows?: number;
        placeholder?: string;
        warning?: string | ((ctx: { value: any; values: any }) => string | undefined);
        tooltipText?: string;
        llmDiffActive?: boolean;
        llmDiffOriginalValue?: any;
        llmDiffFieldName?: string;
        llmDiffOnRevert?: (fieldName: string) => Promise<void>;
    };
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

function SupplierLlmCustomField({ options, stateProps, children, sx }: SupplierLlmCustomFieldProps) {
    const { t } = useTranslation();

    const label = t(
        options.label ??
        options.name.charAt(0).toUpperCase() + options.name.slice(1)
    );

    const onChangeFunction =
        stateProps?.setState != undefined
            ? (event: React.ChangeEvent<HTMLInputElement>) => {
                stateProps?.setState(event.target.value);
            }
            : stateProps?.handleChange;

    let value = stateProps?.state ?? get(stateProps?.values ?? {}, options.name, '');

    if (
        options.select &&
        !options.isMulti &&
        (typeof value === 'object' || value === undefined)
    ) {
        value = '';
    }

    const [selectOpen, setSelectOpen] = useState(false);

    useEffect(() => {
        if (options.selectOpen !== undefined) {
            setSelectOpen(options.selectOpen);
        }
    }, [options.selectOpen]);

    const handleSelectOpen = () => {
        setSelectOpen(true);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(true);
        }
    };

    const handleSelectClose = () => {
        setSelectOpen(false);
        if (typeof options?.setSelectOpen === 'function') {
            options?.setSelectOpen(false);
        }
    };

    const isTouched = get(stateProps?.touched, options.name);
    const error = get(stateProps?.errors, options.name);
    const hasSubmitted = (stateProps?.submitCount ?? 0) > 0;
    const showError = (isTouched || hasSubmitted) && error;

    // --- generic warning logic ---
    const values = stateProps?.values ?? {};
    const isEmpty = value === '' || value === null || typeof value === 'undefined';

    let warningText = '';

    if (!showError && options.warning && (isTouched || hasSubmitted)) {
        if (typeof options.warning === 'string') {
            // only warn when empty
            if (isEmpty) {
                warningText = options.warning;
            }
        } else if (typeof options.warning === 'function') {
            warningText = options.warning({ value, values }) ?? '';
        }
    }

    // --- info from tooltip when FormInfoButton is active ---
    const status = stateProps?.status ?? {};
    const showAllTooltips = Boolean(status.showAllTooltips);
    const tooltipText = options.tooltipText;

    const showTooltipInfo = showAllTooltips && !!tooltipText && !showError && !warningText;

    const helperText = showError ? error : warningText || (showTooltipInfo ? tooltipText : '');

    const isError = Boolean(showError);
    const isWarning = !isError && Boolean(warningText);
    const isInfo = !isError && !isWarning && showTooltipInfo;

    const diffActive = Boolean(options.llmDiffActive);
    const diffOriginalValue = formatSupplierLlmPreviewValue(options.llmDiffOriginalValue);

    const baseSx: SxProps<Theme> = {
        '&.MuiFormControl-root': {
            marginTop: options?.topMargin ?? '16px',
        },
        '& .MuiOutlinedInput-root': {
            height:
                options?.height == null ? 'auto' : options.height + '  !important',
        },
        '& label.MuiInputLabel-root': {
            marginTop: options?.height == null ? '0' : '-5px  !important',
        },
        ...(isWarning && {
            '& .MuiFormHelperText-root': {
                color: 'warning.main',
            },
        }),
        ...(isInfo && {
            '& .MuiFormHelperText-root': {
                color: 'info.main',
            },
        }),
    };

    const diffSx = diffActive
    ? {
        '& .MuiOutlinedInput-root': {
            position: 'relative',
            '& fieldset': {
                borderColor: '#86bde8',
            },
            '&:hover fieldset': {
                borderColor: '#86bde8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#86bde8',
            },
            '& .supplier-llm-revert-button': {
                opacity: 0,
                transition: 'opacity 120ms ease-in-out',
            },
            '&:hover .supplier-llm-revert-button': {
                opacity: 1,
            },
        },
        '& .MuiInputBase-input': {
            paddingTop: options.multiLine ? '22px !important' : '22px !important',
            paddingBottom: options.multiLine ? '8px !important' : '7px !important',
        },
        '& .MuiSelect-select': {
            paddingTop: '22px !important',
            paddingBottom: '7px !important',
        },
        '& textarea.MuiInputBase-input': {
            paddingTop: '22px !important',
        },
    }
    : {};

    return (
        <TextField
            label={label}
            name={options.name}
            value={value}
            className={
                options.className ??
                'custom-field-' + label?.toLowerCase().replace(' ', '-')
            }
            onBlur={stateProps?.handleBlur}
            multiline={options.multiLine ?? false}
            rows={options.rows}
            maxRows={options.maxRows}
            placeholder={options.placeholder}
            onChange={onChangeFunction}
            inputProps={{ readOnly: options.disabled }}
            select={options.select ?? false}
            SelectProps={{
                multiple: options.isMulti ?? false,
                open: selectOpen,
                onOpen: handleSelectOpen,
                onClose: handleSelectClose,
            }}
            InputLabelProps={diffActive ? { shrink: true } : undefined}
            InputProps={diffActive ? {
                startAdornment: (
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            top: 6,
                            left: 14,
                            right: options.select ? 70 : 46,
                            zIndex: 2,
                            //color: '#86bde8',
                            fontStyle: 'italic',
                            fontSize: '0.68rem',
                            opacity: 0.8,
                            fontWeight: 500,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            //textDecoration: 'line-through',
                            pointerEvents: 'none',
                        }}
                    >
                        {diffOriginalValue}
                    </Box>
                ),
                endAdornment: (
                    <IconButton
                        className="supplier-llm-revert-button"
                        size="small"
                        title="Revert to previous value"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (options.llmDiffFieldName && options.llmDiffOnRevert) {
                                options.llmDiffOnRevert(options.llmDiffFieldName);
                            }
                        }}
                        sx={{
                            mr: options.select ? 2.25 : 0,
                            color: '#86bde8',
                        }}
                    >
                        <RestoreIcon fontSize="small" />
                    </IconButton>
                ),
            } : undefined}
            helperText={helperText}
            error={isError}
            type={options.type ?? 'text'}
            variant="outlined"
            fullWidth
            sx={[
                baseSx,
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                diffSx,
            ]}
            margin="normal"
        >
            {children}
        </TextField>
    );
}


/**
 * Supplier LLM API popup/config helpers.
 * Keep future changes to the API behavior mostly in this block.
 */
type SupplierLlmAiConfig = {
    url: string;
    apiKey: string;
    model: string;
    assistantPrefill: string;
    promptCacheKey: string;
    promptCacheRetention: string;
};

type SupplierLlmAiAttachmentKind = "pdf" | "image" | "text" | "unsupported";

type SupplierLlmAiAttachment = {
    id: string;
    fileName: string;
    mimeType: string;
    size: number;
    kind: SupplierLlmAiAttachmentKind;
    text?: string;
    dataUrl?: string;
    truncated?: boolean;
    omittedReason?: string;
};

const supplierLlmAiTextAttachmentMaxChars = 30000;
const supplierLlmAiImageAttachmentMaxBytes = 8 * 1024 * 1024;
const supplierLlmAiPdfAttachmentMaxBytes = 50 * 1024 * 1024;
const supplierLlmAiAttachmentAccept = [
    "application/pdf",
    ".pdf",
    "image/*",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/json",
    "application/xml",
    ".txt",
    ".md",
    ".markdown",
    ".csv",
    ".tsv",
    ".json",
    ".jsonl",
    ".xml",
    ".html",
    ".htm",
    ".yaml",
    ".yml",
    ".js",
    ".jsx",
    ".ts",
    ".tsx",
    ".css",
    ".scss",
    ".sql",
].join(",");

const supplierLlmAiTextAttachmentMimeTypes = [
    "application/json",
    "application/xml",
    "application/x-yaml",
    "application/yaml",
    "text/",
];

const supplierLlmAiTextAttachmentExtensions = [
    ".txt",
    ".md",
    ".markdown",
    ".csv",
    ".tsv",
    ".json",
    ".jsonl",
    ".xml",
    ".html",
    ".htm",
    ".yaml",
    ".yml",
    ".js",
    ".jsx",
    ".ts",
    ".tsx",
    ".css",
    ".scss",
    ".sql",
];

const supplierLlmAiConfigStorageKey = "supplierLlmAiConfig";
const supplierLlmAiConfigChangedEvent = "supplierLlmAiConfigChanged";

const supplierLlmAiModelOptions = [
    "gpt-5.5",
    "gpt-5.4-mini",
    "gpt-5.4-nano",
];

const supplierLlmAiModelOptionMeta: Record<string, { group: string; description: string }> = {
    "gpt-5.5": { group: "Frontier", description: "Best current frontier model for complex work." },
    "gpt-5.4-mini": { group: "Cost-efficient", description: "Balanced cheap model for coding, reasoning, vision, and tool use." },
    "gpt-5.4-nano": { group: "Cost-efficient", description: "Lowest-latency, high-volume GPT-5.4 option." },
  

};

const supplierLlmAiDefaultConfig: SupplierLlmAiConfig = {
    url: "https://api.openai.com/v1/chat/completions",
    apiKey: "",
    model: "gpt-5.4-mini",
    assistantPrefill: '{"values":{"',
    promptCacheKey: "dacsa-supplier-form-v1",
    promptCacheRetention: "",
};

function SupplierLlmAiConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [config, setConfig] = useSupplierLlmAiConfig();
    const [draftConfig, setDraftConfig] = useState<SupplierLlmAiConfig>(config);

    useEffect(() => {
        if (open) {
            setDraftConfig(config);
        }
    }, [config, open]);

    const handleChange = (fieldName: keyof SupplierLlmAiConfig) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraftConfig((previousConfig) => ({ ...previousConfig, [fieldName]: event.target.value }));
    };

    const handleSave = () => {
        setConfig({
            url: draftConfig.url.trim() || supplierLlmAiDefaultConfig.url,
            apiKey: draftConfig.apiKey.trim(),
            model: draftConfig.model.trim() || supplierLlmAiDefaultConfig.model,
            assistantPrefill: draftConfig.assistantPrefill.trim(),
            promptCacheKey: draftConfig.promptCacheKey.trim() || supplierLlmAiDefaultConfig.promptCacheKey,
            promptCacheRetention: draftConfig.promptCacheRetention.trim(),
        });
        onClose();
    };

    const handleReset = () => {
        setDraftConfig(supplierLlmAiDefaultConfig);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>AI configuration</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="API URL"
                        value={draftConfig.url}
                        onChange={handleChange("url")}
                        fullWidth
                        helperText="Include protocol and port if needed, for example http://localhost:11434/v1/chat/completions."
                    />
                    <TextField
                        label="API key"
                        value={draftConfig.apiKey}
                        onChange={handleChange("apiKey")}
                        fullWidth
                        type="password"
                        autoComplete="off"
                        helperText="Stored in localStorage. Leave empty for local endpoints that do not require a key."
                    />
                    <Autocomplete
                        freeSolo
                        autoHighlight
                        options={supplierLlmAiModelOptions}
                        value={draftConfig.model}
                        inputValue={draftConfig.model}
                        groupBy={(option) => supplierLlmAiModelOptionMeta[option]?.group ?? "Custom"}
                        onInputChange={(_, nextInputValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextInputValue }));
                        }}
                        onChange={(_, nextValue) => {
                            setDraftConfig((previousConfig) => ({ ...previousConfig, model: nextValue ?? "" }));
                        }}
                        renderOption={(props, option) => {
                            const meta = supplierLlmAiModelOptionMeta[option];

                            return (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Box component="span" fontWeight={600}>{option}</Box>
                                        {meta?.description ? (
                                            <Box component="span" color="text.secondary" fontSize="0.8rem" ml={1}>
                                                {meta.description}
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Model"
                                fullWidth
                                helperText="Choose a common OpenAI model or type any OpenAI-compatible model name."
                            />
                        )}
                    />
                    <TextField
                        label="Assistant prefill"
                        value={draftConfig.assistantPrefill}
                        onChange={handleChange("assistantPrefill")}
                        fullWidth
                        helperText={'Optional. For llama.cpp, try {"values":{ to make the model continue directly inside the JSON values object.'}
                    />
                    <TextField
                        label="Prompt cache key"
                        value={draftConfig.promptCacheKey}
                        onChange={handleChange("promptCacheKey")}
                        fullWidth
                        helperText="Optional for OpenAI. Use a stable key for requests with the same long prefix."
                    />
                    <TextField
                        label="Prompt cache retention"
                        value={draftConfig.promptCacheRetention}
                        onChange={handleChange("promptCacheRetention")}
                        fullWidth
                        helperText='Optional for OpenAI. Use 24h when supported, or leave empty for the default.'
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset}>Reset defaults</Button>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

function SupplierLlmAiRunDialog({
    open,
    onClose,
    buildPrompt,
    applyLlmResponseText,
}: {
    open: boolean;
    onClose: () => void;
    buildPrompt: () => string;
    applyLlmResponseText: (responseText: string) => Promise<SupplierLlmApplyResult>;
}) {
    const [config] = useSupplierLlmAiConfig();
    const [userPrompt, setUserPrompt] = useState("");
    const [attachments, setAttachments] = useState<SupplierLlmAiAttachment[]>([]);
    const [isRunning, setIsRunning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    const [attachmentErrorMessage, setAttachmentErrorMessage] = useState("");

    const supportedAttachments = attachments.filter((attachment) => attachment.kind !== "unsupported");
    const canRun = (Boolean(userPrompt.trim()) || supportedAttachments.length > 0) && Boolean(config.url.trim()) && !isRunning;

    const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";

        if (files.length === 0) {
            return;
        }

        setAttachmentErrorMessage("");

        try {
            const nextAttachments = await readSupplierLlmAiAttachmentsFromFiles(files);
            setAttachments((previousAttachments) => [...previousAttachments, ...nextAttachments]);
        } catch (error) {
            console.error("Failed to read supplier LLM attachments.", error);
            setAttachmentErrorMessage(error instanceof Error ? error.message : "Could not read one or more attachments.");
        }
    };

    const handleRemoveAttachment = (attachmentId: string) => {
        setAttachments((previousAttachments) => previousAttachments.filter((attachment) => attachment.id !== attachmentId));
    };

    const handleRun = async () => {
        if (!canRun) {
            return;
        }

        setIsRunning(true);
        setErrorMessage("");
        setStatusMessage("");

        try {
            const responseText = await requestSupplierLlmAiResponse({
                config,
                userPrompt,
                formPrompt: buildPrompt(),
                attachments: supportedAttachments,
            });
            const applyResult = await applyLlmResponseText(responseText);

            if (applyResult === 'no-fields') {
                setErrorMessage('The AI response did not contain matching Formik fields. Expected either { "values": { ... } } or a JSON object with Formik field names.');
                return;
            }

            if (applyResult === 'no-changes') {
                setStatusMessage("AI response was valid, but it did not change any current field values.");
                return;
            }

            setStatusMessage("AI response applied to the form.");
        } catch (error) {
            console.error("Failed to run supplier LLM request.", error);
            setErrorMessage(error instanceof Error ? error.message : "Could not run the AI request.");
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <Dialog open={open} onClose={isRunning ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle>Run AI update</DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <TextField
                        label="Prompt"
                        value={userPrompt}
                        onChange={(event) => setUserPrompt(event.target.value)}
                        fullWidth
                        multiline
                        rows={7}
                        disabled={isRunning}
                        placeholder="Example: Fill in missing supplier fields from this text, PDF, or image..."
                    />
                    <Box>
                        <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} disabled={isRunning}>
                            Attach files/images
                            <input hidden type="file" multiple accept={supplierLlmAiAttachmentAccept} onChange={handleAttachmentChange} />
                        </Button>
                        <FormHelperText>
                            PDFs and images are sent as multimodal attachments. Text-like files are inserted into the prompt.
                        </FormHelperText>
                        {attachmentErrorMessage ? <Alert severity="error" sx={{ mt: 1 }}>{attachmentErrorMessage}</Alert> : null}
                        {attachments.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                                {attachments.map((attachment) => (
                                    <Chip
                                        key={attachment.id}
                                        label={getSupplierLlmAiAttachmentChipLabel(attachment)}
                                        onDelete={isRunning ? undefined : () => handleRemoveAttachment(attachment.id)}
                                        color={attachment.kind === "unsupported" ? "warning" : "default"}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        ) : null}
                    </Box>
                    {isRunning ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={20} />
                            <span>Calling AI and applying JSON response...</span>
                        </Box>
                    ) : null}
                    {statusMessage ? <Alert severity="success">{statusMessage}</Alert> : null}
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRunning}>Close</Button>
                <Button onClick={handleRun} variant="contained" disabled={!canRun}>Run</Button>
            </DialogActions>
        </Dialog>
    );
}
function useSupplierLlmAiConfig(): [SupplierLlmAiConfig, (nextConfig: SupplierLlmAiConfig) => void] {
    const [config, setConfigState] = useState<SupplierLlmAiConfig>(() => getStoredSupplierLlmAiConfig());

    const setConfig = useCallback((nextConfig: SupplierLlmAiConfig) => {
        setConfigState(nextConfig);
        saveStoredSupplierLlmAiConfig(nextConfig);
    }, []);

    useEffect(() => {
        const refreshConfig = () => setConfigState(getStoredSupplierLlmAiConfig());
        const handleStorage = (event: StorageEvent) => {
            if (event.key === supplierLlmAiConfigStorageKey) {
                refreshConfig();
            }
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener(supplierLlmAiConfigChangedEvent, refreshConfig);

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener(supplierLlmAiConfigChangedEvent, refreshConfig);
        };
    }, []);

    return [config, setConfig];
}

function getStoredSupplierLlmAiConfig(): SupplierLlmAiConfig {
    if (typeof window === "undefined") {
        return supplierLlmAiDefaultConfig;
    }

    try {
        const rawConfig = window.localStorage.getItem(supplierLlmAiConfigStorageKey);
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : {};

        return {
            ...supplierLlmAiDefaultConfig,
            ...parsedConfig,
            url: parsedConfig?.url || supplierLlmAiDefaultConfig.url,
            apiKey: parsedConfig?.apiKey || "",
            model: parsedConfig?.model || supplierLlmAiDefaultConfig.model,
            assistantPrefill: parsedConfig?.assistantPrefill || "",
            promptCacheKey: parsedConfig?.promptCacheKey || supplierLlmAiDefaultConfig.promptCacheKey,
            promptCacheRetention: parsedConfig?.promptCacheRetention || "",
        };
    } catch (error) {
        console.error("Failed to load supplier LLM AI config from localStorage.", error);
        return supplierLlmAiDefaultConfig;
    }
}

function saveStoredSupplierLlmAiConfig(config: SupplierLlmAiConfig) {
    if (typeof window === "undefined") {
        return;
    }

    window.localStorage.setItem(supplierLlmAiConfigStorageKey, JSON.stringify(config));
    window.dispatchEvent(new Event(supplierLlmAiConfigChangedEvent));
}

async function requestSupplierLlmAiResponse({
    config,
    userPrompt,
    formPrompt,
    attachments = [],
}: {
    config: SupplierLlmAiConfig;
    userPrompt: string;
    formPrompt: string;
    attachments?: SupplierLlmAiAttachment[];
}) {
    const response = await fetch(config.url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(config.apiKey ? { Authorization: `Bearer ${config.apiKey}` } : {}),
        },
        body: JSON.stringify(getSupplierLlmAiRequestBody({ config, userPrompt, formPrompt, attachments })),
    });

    const responseBodyText = await response.text();

    if (!response.ok) {
        throw new Error(getSupplierLlmApiErrorMessage(response.status, responseBodyText));
    }

    const responseBody = safeJsonParse(responseBodyText);
    const content = getSupplierLlmAiResponseContent(responseBody) ?? responseBodyText;
    const normalizedContent = applySupplierLlmAssistantPrefill(content, config.assistantPrefill);
    const jsonText = extractJsonObjectText(normalizedContent);

    if (!jsonText || jsonText === normalizedContent.trim() && !normalizedContent.trim().startsWith("{") && !normalizedContent.trim().startsWith("```")) {
        throw new Error("The AI response did not contain a JSON object.");
    }

    return jsonText;
}

function getSupplierLlmAiRequestBody({
    config,
    userPrompt,
    formPrompt,
    attachments = [],
}: {
    config: SupplierLlmAiConfig;
    userPrompt: string;
    formPrompt: string;
    attachments?: SupplierLlmAiAttachment[];
}) {
    const assistantPrefill = config.assistantPrefill.trim();
    const promptText = buildSupplierLlmAiPrompt(userPrompt, formPrompt, attachments);
    const attachmentParts = getSupplierLlmAiAttachmentMessageParts(attachments);
    const messages: Array<{ role: string; content: any }> = [
        {
            role: "system",
            content: "You update Formik form values. Continue any provided assistant prefill. Return only valid JSON. Prefer the { values: { ... } } shape. Do not return markdown, codeblocks, explanations, or links.",
        },
        {
            role: "user",
            content: attachmentParts.length > 0
                ? [
                    ...attachmentParts,
                    { type: "text", text: promptText },
                ]
                : promptText,
        },
    ];

    if (assistantPrefill) {
        messages.push({
            role: "assistant",
            content: assistantPrefill,
        });
    }

    const isOpenAiEndpoint = isOpenAiChatCompletionsUrl(config.url);
    const body: Record<string, any> = {
        model: config.model || supplierLlmAiDefaultConfig.model,
        messages,
        temperature: 1,
    };

    if (isOpenAiEndpoint) {
        if (config.promptCacheKey.trim()) {
            body.prompt_cache_key = config.promptCacheKey.trim();
        }

        if (config.promptCacheRetention.trim()) {
            body.prompt_cache_retention = config.promptCacheRetention.trim();
        }
    } else {
        body.cache_prompt = true;
    }

    return body;
}

function getSupplierLlmAiAttachmentMessageParts(attachments: SupplierLlmAiAttachment[]) {
    return attachments.reduce((parts: any[], attachment) => {
        if (attachment.kind === "pdf" && attachment.dataUrl) {
            parts.push({
                type: "file",
                file: {
                    filename: attachment.fileName,
                    file_data: attachment.dataUrl,
                },
            });
        }

        if (attachment.kind === "image" && attachment.dataUrl) {
            parts.push({
                type: "image_url",
                image_url: {
                    url: attachment.dataUrl,
                    detail: "auto",
                },
            });
        }

        return parts;
    }, []);
}

function buildSupplierLlmAiPrompt(userPrompt: string, formPrompt: string, attachments: SupplierLlmAiAttachment[] = []) {
    const attachmentPrompt = buildSupplierLlmAiAttachmentPrompt(attachments);

    return [
        "Form context and return instructions:",
        formPrompt,
        "",
        "User request:",
        userPrompt.trim() || "Use the attached files/images to update the supplier form.",
        ...(attachmentPrompt ? ["", "Attached files/images:", attachmentPrompt] : []),
    ].join("\n");
}

function buildSupplierLlmAiAttachmentPrompt(attachments: SupplierLlmAiAttachment[]) {
    if (attachments.length === 0) {
        return "";
    }

    return attachments.map((attachment, index) => {
        const header = `[Attachment ${index + 1}: ${attachment.fileName}; type=${attachment.mimeType || "unknown"}; size=${formatSupplierLlmAiBytes(attachment.size)}]`;

        if (attachment.kind === "text") {
            return [
                header,
                attachment.truncated ? `[Text content truncated to ${supplierLlmAiTextAttachmentMaxChars} characters.]` : "[Text content]",
                attachment.text ?? "",
            ].join("\n");
        }

        if (attachment.kind === "pdf") {
            return `${header}\n[PDF is attached as a file input. Read the PDF text and page images before deciding supplier form values.]`;
        }

        if (attachment.kind === "image") {
            return `${header}\n[Image is attached as a multimodal image input. Use visible supplier names, address details, contact details, registration numbers, invoice text, labels, and other relevant details.]`;
        }

        return `${header}\n[Attachment not included: ${attachment.omittedReason ?? "Unsupported file type."}]`;
    }).join("\n\n");
}

async function readSupplierLlmAiAttachmentsFromFiles(files: File[]) {
    return Promise.all(files.map(readSupplierLlmAiAttachmentFromFile));
}

async function readSupplierLlmAiAttachmentFromFile(file: File): Promise<SupplierLlmAiAttachment> {
    const baseAttachment = {
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
    };

    if (isSupplierLlmAiPdfFile(file)) {
        if (file.size > supplierLlmAiPdfAttachmentMaxBytes) {
            return {
                ...baseAttachment,
                kind: "unsupported",
                omittedReason: `PDF is larger than ${formatSupplierLlmAiBytes(supplierLlmAiPdfAttachmentMaxBytes)}.`,
            };
        }

        return {
            ...baseAttachment,
            kind: "pdf",
            dataUrl: await readSupplierLlmAiFileAsDataUrl(file),
        };
    }

    if (isSupplierLlmAiImageFile(file)) {
        if (file.size > supplierLlmAiImageAttachmentMaxBytes) {
            return {
                ...baseAttachment,
                kind: "unsupported",
                omittedReason: `Image is larger than ${formatSupplierLlmAiBytes(supplierLlmAiImageAttachmentMaxBytes)}.`,
            };
        }

        return {
            ...baseAttachment,
            kind: "image",
            dataUrl: await readSupplierLlmAiFileAsDataUrl(file),
        };
    }

    if (isSupplierLlmAiTextFile(file)) {
        const fullText = await readSupplierLlmAiFileAsText(file);
        const truncated = fullText.length > supplierLlmAiTextAttachmentMaxChars;
        const text = truncated ? fullText.slice(0, supplierLlmAiTextAttachmentMaxChars) : fullText;

        return {
            ...baseAttachment,
            kind: "text",
            text,
            truncated,
        };
    }

    return {
        ...baseAttachment,
        kind: "unsupported",
        omittedReason: "Only PDFs, images, and text-like files are currently included in AI prompts.",
    };
}

function isSupplierLlmAiPdfFile(file: File) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return mimeType === "application/pdf" || mimeType === "application/x-pdf" || fileName.endsWith(".pdf");
}

function isSupplierLlmAiImageFile(file: File) {
    return file.type.toLowerCase().startsWith("image/");
}

function isSupplierLlmAiTextFile(file: File) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return supplierLlmAiTextAttachmentMimeTypes.some((acceptedMimeType) => mimeType.startsWith(acceptedMimeType)) ||
        supplierLlmAiTextAttachmentExtensions.some((extension) => fileName.endsWith(extension));
}

function readSupplierLlmAiFileAsText(file: File) {
    if (typeof file.text === "function") {
        return file.text();
    }

    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Could not read text attachment."));
        reader.readAsText(file);
    });
}

function readSupplierLlmAiFileAsDataUrl(file: File) {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Could not read attachment."));
        reader.readAsDataURL(file);
    });
}

function getSupplierLlmAiAttachmentChipLabel(attachment: SupplierLlmAiAttachment) {
    const suffix = attachment.kind === "text" && attachment.truncated ? ", truncated" : attachment.kind === "unsupported" ? ", unsupported" : "";
    return `${attachment.fileName} (${attachment.kind}, ${formatSupplierLlmAiBytes(attachment.size)}${suffix})`;
}

function formatSupplierLlmAiBytes(bytes: number) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return "0 B";
    }

    const units = ["B", "KB", "MB", "GB"];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex++;
    }

    const fractionDigits = value >= 10 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(fractionDigits)} ${units[unitIndex]}`;
}

function isOpenAiChatCompletionsUrl(url: string) {
    return /api\.openai\.com/i.test(url);
}

function applySupplierLlmAssistantPrefill(responseText: string, assistantPrefill: string) {
    const trimmedText = responseText.trim();
    const trimmedPrefill = assistantPrefill.trim();

    if (!trimmedPrefill || !trimmedText || trimmedText.startsWith(trimmedPrefill)) {
        return trimmedText;
    }

    if (trimmedText.startsWith("{") && trimmedPrefill.startsWith("{")) {
        return trimmedText;
    }

    return trimmedPrefill + trimmedText;
}

function getSupplierLlmAiResponseContent(responseBody: any) {
    const chatCompletionContent = responseBody?.choices?.[0]?.message?.content;

    if (typeof chatCompletionContent === "string") {
        return chatCompletionContent;
    }

    if (Array.isArray(chatCompletionContent)) {
        return chatCompletionContent.map((contentPart) => contentPart?.text ?? contentPart?.content ?? "").join("\n").trim();
    }

    const responseOutputText = responseBody?.output_text;

    if (typeof responseOutputText === "string") {
        return responseOutputText;
    }

    return null;
}

function getSupplierLlmApiErrorMessage(status: number, responseBodyText: string) {
    const parsedBody = safeJsonParse(responseBodyText);
    const apiMessage = parsedBody?.error?.message ?? parsedBody?.message;

    if (apiMessage) {
        return `AI request failed (${status}): ${apiMessage}`;
    }

    return `AI request failed (${status}).`;
}

function safeJsonParse(text: string) {
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

async function writeTextToClipboard(text: string) {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    if (typeof document === "undefined") {
        throw new Error("Clipboard write is not available.");
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();

    const success = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (!success) {
        throw new Error("Fallback clipboard copy failed.");
    }
}

function parseJsonFromClipboardText(clipboardText: string) {
    const trimmedText = clipboardText.trim();
    const fencedJsonMatch = trimmedText.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const jsonText = fencedJsonMatch?.[1] ?? extractJsonObjectText(trimmedText);

    return JSON.parse(jsonText);
}

function extractJsonObjectText(text: string) {
    const firstBraceIndex = text.indexOf("{");
    const lastBraceIndex = text.lastIndexOf("}");

    if (firstBraceIndex === -1 || lastBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) {
        return text;
    }

    return text.slice(firstBraceIndex, lastBraceIndex + 1);
}

function getFormikValuesFromLlmResponse(parsedResponse: any) {
    if (parsedResponse?.values && typeof parsedResponse.values === "object" && !Array.isArray(parsedResponse.values)) {
        return parsedResponse.values;
    }

    if (parsedResponse?.formValues && typeof parsedResponse.formValues === "object" && !Array.isArray(parsedResponse.formValues)) {
        return parsedResponse.formValues;
    }

    return parsedResponse;
}

function getAllowedFormikValues(nextValues: any, currentValues: any) {
    if (!nextValues || typeof nextValues !== "object" || Array.isArray(nextValues)) {
        return {};
    }

    return supplierLlmEditableFields.reduce((acc: any, fieldName) => {
        if (supplierLlmReadOnlyFields.includes(fieldName)) {
            return acc;
        }

        if (Object.prototype.hasOwnProperty.call(nextValues, fieldName)) {
            acc[fieldName] = nextValues[fieldName];
        }

        return acc;
    }, {});
}

function getChangedFormikValues(allowedValues: any, currentValues: any) {
    return Object.keys(allowedValues).reduce((acc: any, fieldName) => {
        const currentValue = getValueByPath(currentValues, fieldName);
        const nextValue = allowedValues[fieldName];

        if (!areSupplierLlmValuesEqual(currentValue, nextValue)) {
            acc[fieldName] = nextValue;
        }

        return acc;
    }, {});
}

function getNextSupplierLlmChangedFields(previousChangedFields: SupplierLlmChangedFields, changedValues: any, currentValues: any) {
    const nextChangedFields: SupplierLlmChangedFields = { ...previousChangedFields };

    Object.keys(changedValues).forEach((fieldName) => {
        const originalValue = previousChangedFields[fieldName]?.originalValue ?? getValueByPath(currentValues, fieldName);
        const pastedValue = changedValues[fieldName];

        if (areSupplierLlmValuesEqual(originalValue, pastedValue)) {
            delete nextChangedFields[fieldName];
            return;
        }

        nextChangedFields[fieldName] = {
            fieldName,
            originalValue,
            pastedValue,
        };
    });

    return nextChangedFields;
}

function getSupplierLlmDiffOptions(fieldProps: any, formikProps: FormikProps<any>, supplierLlmClipboard: SupplierLlmClipboardController) {
    const fieldName = fieldProps?.name;
    const change = fieldName ? supplierLlmClipboard.changedFields[fieldName] : null;
    const currentValue = fieldName ? getValueByPath(formikProps.values, fieldName) : undefined;
    const isUnsupportedDiffField = Boolean(fieldProps.checkbox || fieldProps.date || fieldProps.disabled);
    const shouldShowDiff = Boolean(fieldName && change && !isUnsupportedDiffField && !areSupplierLlmValuesEqual(change.originalValue, currentValue));

    if (!shouldShowDiff) {
        return {};
    }

    return {
        llmDiffActive: true,
        llmDiffOriginalValue: change.originalValue,
        llmDiffFieldName: fieldName,
        llmDiffOnRevert: supplierLlmClipboard.revertField,
    };
}

function compactSupplierValues(values: any) {
    const sourceValues = values ?? {};

    return supplierLlmEditableFields.reduce((acc: any, fieldName) => {
        if (Object.prototype.hasOwnProperty.call(sourceValues, fieldName)) {
            acc[fieldName] = sourceValues[fieldName];
        }

        return acc;
    }, {});
}

function compactSupplierLabelValueOptions(options: any) {
    if (!Array.isArray(options)) {
        return {};
    }

    return options.reduce((acc: Record<string, any>, option: any) => {
        const label = option?.label ?? option?.Value ?? option?.Name ?? option?.text;
        const value = option?.value ?? option?.Key ?? option?.Id ?? option?.id;

        if (label === undefined || label === null || value === undefined || value === null) {
            return acc;
        }

        acc[String(label)] = value;
        return acc;
    }, {});
}

function areSupplierLlmValuesEqual(firstValue: any, secondValue: any) {
    return normalizeSupplierLlmValue(firstValue) === normalizeSupplierLlmValue(secondValue);
}

function normalizeSupplierLlmValue(value: any) {
    if (value === undefined || value === null) {
        return "";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value).trim();
}

function formatSupplierLlmPreviewValue(value: any) {
    if (value === undefined || value === null || value === "") {
        return "Empty";
    }

    if (typeof value === "boolean") {
        return value ? "True" : "False";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

function getValueByPath(values: any, path: string) {
    return path.split(".").reduce((acc, pathPart) => acc?.[pathPart], values);
}

function getPageNameText(pageName: string | { text: string; after?: React.ReactNode }) {
    return typeof pageName === "string" ? pageName : pageName.text;
}

const toBoolean = (value: any) => {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return Boolean(value);
};

export default EditSupplierAiButtons;